// src/constants/commandRegistry.js
// Static command registry for CommandPalette command mode (/ prefix).
// Each entry: { id, label, keywords, icon, group, action(navigate, practiceId) }
// Groups: 'navigate' | 'create' | 'settings'

const commandRegistry = [
    // ── Navigate: Patient Tables ──────────────────────────────────────
    {
        id: 'go-new-leads',
        label: 'Go to New Leads',
        keywords: ['new', 'enquiries', 'new leads'],
        icon: 'eva:person-add-fill',
        group: 'navigate',
        action: (navigate) => navigate('/dashboard/tables/new-lead-table'),
    },
    {
        id: 'go-booked',
        label: 'Go to Consultation Booked',
        keywords: ['booked', 'consultation booked'],
        icon: 'eva:calendar-fill',
        group: 'navigate',
        action: (navigate) => navigate('/dashboard/tables/consultant-booked-table'),
    },
    {
        id: 'go-attended',
        label: 'Go to Consultation Attended',
        keywords: ['attended', 'consultation attended'],
        icon: 'eva:checkmark-circle-fill',
        group: 'navigate',
        action: (navigate) => navigate('/dashboard/tables/consultant-attended-table'),
    },
    {
        id: 'go-treatment-started',
        label: 'Go to Treatment Started',
        keywords: ['treatment', 'active', 'treatment started'],
        icon: 'eva:heart-fill',
        group: 'navigate',
        action: (navigate) => navigate('/dashboard/tables/treatment-started-table'),
    },
    {
        id: 'go-completed',
        label: 'Go to Completed',
        keywords: ['closed', 'completed', 'done'],
        icon: 'eva:award-fill',
        group: 'navigate',
        action: (navigate) => navigate('/dashboard/tables/completed-table'),
    },
    {
        id: 'go-fta',
        label: 'Go to FTA',
        keywords: ['fta', 'failed', 'failed to attend', 'no show'],
        icon: 'eva:close-circle-fill',
        group: 'navigate',
        action: (navigate) => navigate('/dashboard/tables/failed-to-attend-table'),
    },
    {
        id: 'go-archived',
        label: 'Go to Archived',
        keywords: ['archive', 'archived'],
        icon: 'eva:archive-fill',
        group: 'navigate',
        action: (navigate) => navigate('/dashboard/tables/archive-table'),
    },
    {
        id: 'go-all-patients',
        label: 'Go to All Patients',
        keywords: ['all patients', 'all leads'],
        icon: 'eva:people-fill',
        group: 'navigate',
        action: (navigate) => navigate('/dashboard/tables/all-patients'),
    },
    {
        id: 'go-no-task',
        label: 'Go to No Task Assigned',
        keywords: ['no task', 'unassigned'],
        icon: 'eva:minus-circle-fill',
        group: 'navigate',
        action: (navigate) => navigate('/dashboard/tables/no-task-assigned-table'),
    },
    // ── Navigate: App Sections ────────────────────────────────────────
    {
        id: 'go-dashboard',
        label: 'Go to Dashboard',
        keywords: ['home', 'dashboard', 'start'],
        icon: 'eva:home-fill',
        group: 'navigate',
        action: (navigate) => navigate('/dashboard/app'),
    },
    {
        id: 'go-tasks',
        label: 'Go to Tasks',
        keywords: ['tasks', 'reminders', 'todos', 'to do'],
        icon: 'fluent:tasks-app-28-regular',
        group: 'navigate',
        contextualPanel: 'tasks',
        action: (navigate) => navigate('/dashboard/tasks'),
    },
    {
        id: 'go-whatsapp',
        label: 'Go to WhatsApp',
        keywords: ['whatsapp', 'wa', 'messages', 'chat'],
        icon: 'eva:message-circle-fill',
        group: 'navigate',
        action: (navigate, practiceId) => navigate(`/dashboard/practice/${practiceId}/whatsapp`),
    },
    {
        id: 'go-calendar',
        label: 'Go to Calendar',
        keywords: ['calendar', 'appointments', 'schedule'],
        icon: 'eva:calendar-fill',
        group: 'navigate',
        action: (navigate) => navigate('/dashboard/calendar'),
    },
    {
        id: 'go-kanban',
        label: 'Go to Kanban',
        keywords: ['kanban', 'pipeline', 'board'],
        icon: 'eva:layout-fill',
        group: 'navigate',
        action: (navigate) => navigate('/dashboard/kanban'),
    },
    {
        id: 'go-campaigns',
        label: 'Go to Campaigns',
        keywords: ['campaigns', 'marketing', 'broadcast'],
        icon: 'eva:email-fill',
        group: 'navigate',
        contextualPanel: 'campaigns',
        action: (navigate) => navigate('/dashboard/campaigns-manager/campaigns'),
    },
    {
        id: 'go-analytics',
        label: 'Go to Analytics Overview',
        keywords: ['analytics', 'reports', 'overview', 'stats'],
        icon: 'eva:bar-chart-fill',
        group: 'navigate',
        action: (navigate) => navigate('/dashboard/analytics-reports/overview'),
    },
    {
        id: 'go-analytics-treatment',
        label: 'Go to Analytics by Treatment',
        keywords: ['analytics treatment', 'treatment report'],
        icon: 'eva:bar-chart-fill',
        group: 'navigate',
        action: (navigate) => navigate('/dashboard/analytics-reports/by-treatment'),
    },
    {
        id: 'go-analytics-practitioner',
        label: 'Go to Analytics by Practitioner',
        keywords: ['analytics practitioner', 'practitioner report', 'staff report'],
        icon: 'eva:bar-chart-fill',
        group: 'navigate',
        action: (navigate) => navigate('/dashboard/analytics-reports/by-practitioner'),
    },
    {
        id: 'go-analytics-source',
        label: 'Go to Analytics by Source',
        keywords: ['analytics source', 'source report', 'referral report'],
        icon: 'eva:bar-chart-fill',
        group: 'navigate',
        action: (navigate) => navigate('/dashboard/analytics-reports/by-source'),
    },
    {
        id: 'go-financial',
        label: 'Go to Financial Tracker',
        keywords: ['financial', 'finance', 'payments', 'tracker'],
        icon: 'eva:credit-card-fill',
        group: 'navigate',
        action: (navigate) => navigate('/dashboard/financial-tracker'),
    },
    {
        id: 'go-call-logs',
        label: 'Go to Call Logs',
        keywords: ['calls', 'call logs', 'phone'],
        icon: 'eva:phone-fill',
        group: 'navigate',
        action: (navigate) => navigate('/dashboard/call-logs'),
    },
    // ── Create ────────────────────────────────────────────────────────
    {
        id: 'new-lead',
        label: 'Create New Lead',
        keywords: ['new lead', 'add lead', 'add patient', 'new patient', 'create lead'],
        icon: 'eva:person-add-fill',
        group: 'create',
        // action is null — handled specially in CommandPalette to open CreateLeadDialog
        action: null,
    },
    {
        id: 'new-task',
        label: 'Create New Task',
        keywords: ['new task', 'reminder', 'todo', 'add task', 'create task'],
        icon: 'eva:plus-square-fill',
        group: 'create',
        action: (navigate) => navigate('/dashboard/tasks'),
    },
    {
        id: 'new-campaign',
        label: 'Create New Campaign',
        keywords: ['new campaign', 'send message', 'marketing', 'broadcast'],
        icon: 'eva:email-fill',
        group: 'create',
        action: (navigate) => navigate('/dashboard/campaigns-manager/builder'),
    },
    // ── Settings ──────────────────────────────────────────────────────
    {
        id: 'settings-practice',
        label: 'Settings: Practice Details',
        keywords: ['settings', 'practice details', 'practice info'],
        icon: 'eva:settings-fill',
        group: 'settings',
        action: (navigate, practiceId) => navigate(`/dashboard/user/practice/${practiceId}`),
    },
    {
        id: 'settings-treatments',
        label: 'Settings: Treatments',
        keywords: ['settings', 'treatments', 'treatment list'],
        icon: 'eva:settings-fill',
        group: 'settings',
        action: (navigate) => navigate('/dashboard/user/practice-treatment'),
    },
    {
        id: 'settings-users',
        label: 'Settings: Users & Staff',
        keywords: ['settings', 'users', 'staff', 'team', 'people'],
        icon: 'eva:settings-fill',
        group: 'settings',
        contextualPanel: 'staff',
        action: (navigate) => navigate('/dashboard/user/practice-user'),
    },
    {
        id: 'settings-templates',
        label: 'Settings: Message Templates',
        keywords: ['settings', 'templates', 'messages', 'whatsapp templates'],
        icon: 'eva:settings-fill',
        group: 'settings',
        action: (navigate) => navigate('/dashboard/user/message'),
    },
    {
        id: 'settings-notifications',
        label: 'Settings: Notifications',
        keywords: ['settings', 'notifications', 'alerts'],
        icon: 'eva:settings-fill',
        group: 'settings',
        action: (navigate) => navigate('/dashboard/user/notification-settings'),
    },
];

export const GROUP_LABELS = {
    navigate: 'Navigate',
    create: 'Create',
    settings: 'Settings',
};

// Fuzzy filter: command matches if label or any keyword contains every word in the query
export const filterCommands = (query) => {
    const q = query.toLowerCase().trim();
    if (!q) return commandRegistry;
    return commandRegistry.filter((cmd) => {
        const haystack = [cmd.label, ...cmd.keywords].join(' ').toLowerCase();
        return q.split(' ').every((word) => haystack.includes(word));
    });
};

export default commandRegistry;
